import { ReactNode, useCallback } from "react"
import { useTranslation } from "react-i18next"

import { IListItem, ListItemType } from "components/List/List"
import { useAssetPinnedFields } from "../stores/assetPinnedFields"
import Asset, { AssetOwnershipType } from "models/Asset"
import NoValue from "components/NoValue/NoValue"
import Features from "components/Features/Features"
import { getTranslation } from "utils/translations"
import { useTenantSettings } from "stores/tenantSettings"
import FreightUnitsItem from "components/FreightUnits/FreightUnitsItem"
import ListKeyValue from "components/List/ListKeyValue"
import { getAssetDisposalReasonLabel, getPurposeLabel } from "utils/utils"
import ConnectedAsset from "models/ConnectedAsset"
import AssetCollectionTable from "components/ConnectedAssets/AssetCollectionTable"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import Text from "components/Text/Text"
import AssetStoredStatus from "components/AssetStoredStatus/AssetStoredStatus"
import { ApiLinkEntity, ApiLinkRel, ApiServiceName, ArticleType } from "config/constants"
import { ArticleAny } from "@/types"
import TenantEmployee from "models/TenantEmployee"
import { IUseAssetInitialSaleOrRental } from "hooks/useAssetInitialSaleOrRental"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import reservationSidebars from "stores/reservationSidebars"
import ColorModel from "models/Color"
import { usePermissions } from "stores/permissions"
import useAssetTechnicalDataFields, { AssetTechnicalDataField } from "./useAssetTechnicalDataFields"
import { IAssetSummaryInvalidFields } from "components/AssetSummary/AssetSummary"
import { Flex } from "components/Flex"
import Address from "models/Address"


export enum AssetField {
    lmid = "lmid",
    serialNumber = "serialNumber",
    serialNumberMachine = "serialNumberMachine",
    drawingNumber = "drawingNumber",
    manufacturerCode = "manufacturerCode",
    inventoryNumber = "inventoryNumber",
    labelIds = "labelIds",
    internalOrderNumber = "internalOrderNumber",
    expectedToBeAvailableOn = "expectedToBeAvailableOn",
    expectedToBeReturnedOn = "expectedToBeReturnedOn",
    supplierOrderNumber = "supplierOrderNumber",
    reasonForDisposal = "reasonForDisposal",
    disposalDate = "disposalDate",
    disposalDescription = "disposalDescription",
    profitCenter = "profitCenter",
    purpose = "purpose",
    currentHolder = "currentHolder",
    buyBackCustomer = "buyBackCustomer",
    buyBackSalesPerson = "buyBackSalesPerson",
    buyBackReference = "buyBackReference",
    lendingCompanyName = "lendingCompanyName",
    lendingCompanyAddress = "lendingCompanyAddress",
    lendingCompanyContactPerson = "lendingCompanyContactPerson",
    lendingCompanyTelephone = "lendingCompanyTelephone",
    lendingCompanyEmail = "lendingCompanyEmail",
    relevantForSalesStatistic = "relevantForSalesStatistic",
    relatedRequest = "relatedRequest",
    reportAsset = "reportAsset",
    internalNotes = "internalNotes",
    constructionYear = "constructionYear",
    operatingHours = "operatingHours",
    operationalState = "operationalState",
    condition = "condition",
    features = "features",
    freightUnits = "freightUnits",
    connectedAssets = "connectedAssets",
    stateOfStorage = "stateOfStorage",
    coordinates = "coordinates",
    homebase = "homebase",
    shelfStorage = "shelfStorage",
    shelfStorageDescription = "shelfStorageDescription",
    referenceNumberSale = "referenceNumberSale",
    referenceNumberRental = "referenceNumberRental",
    referenceValue = "referenceValue",
    referenceValueCorrection = "referenceValueCorrection",
    referenceValueCorrected = "referenceValueCorrected",
    monthlyRentalCosts = "monthlyRentalCosts",
    listPrice = "listPrice",
    listPriceDate = "listPriceDate",
    listPriceDiscount = "listPriceDiscount",
    listPriceSalesSupport = "listPriceSalesSupport",
    recommendedSalesPrice = "recommendedSalesPrice",
    recommendedSalesPriceAdjusted = "recommendedSalesPriceAdjusted",
    initialCondition = "initialCondition",
    assetOrigin = "assetOrigin",
    productGroup = "productGroup",
    assetCategory = "assetCategory",
    assetType = "assetType",
}

export default function useAssetFields({
    asset,
    article,
    connectedAssets,
    onCopyFeatures,
    displayStatus,
    displayStatusLoading,
    homebaseWarehouseDescription,
    tenantEmployees,
    initialSaleOrRental,
    operationHoursLogButton,
    color,
    eventAddress,
    pinnable,
    pinnedBlock,
    invalidFields,
    discountCalculated,
    salesPrice,
    salesPriceAdjustedCondition,
}: {
    asset: Asset
    article: ArticleAny
    connectedAssets?: ConnectedAsset[]
    onCopyFeatures?: (features: string) => Promise<void>
    displayStatus?: AssetDisplayStatus
    displayStatusLoading?: boolean
    homebaseWarehouseDescription?: string | null
    tenantEmployees?: TenantEmployee[]
    initialSaleOrRental?: IUseAssetInitialSaleOrRental
    operationHoursLogButton?: ReactNode
    color?: ColorModel | null
    eventAddress?: Address
    invalidFields?: IAssetSummaryInvalidFields
    pinnable?: boolean
    pinnedBlock?: boolean
    discountCalculated?: number | null
    salesPrice?: number | null
    salesPriceAdjustedCondition?: number | null
}) {
    const { t } = useTranslation()
    const { pinnedFields, toggleFieldPin } = useAssetPinnedFields()
    const { getField: getTechnicalDataField } = useAssetTechnicalDataFields({ asset, article, color, pinnable })
    const { tenantPrimaryLanguageIsoCode, tenantSettings } = useTenantSettings()
    const { checkGlobalPermission } = usePermissions()
    const currency = tenantSettings?.defaultCurrency

    const _salesPrice = salesPrice !== undefined ? salesPrice : asset.assetListPrice?.salesPrice
    const _salesPriceAdjustedCondition = typeof salesPriceAdjustedCondition !== "undefined" ? salesPriceAdjustedCondition : asset.assetListPrice?.salesPriceAdjustedCondition

    const getField = useCallback((field: AssetField | AssetTechnicalDataField): IListItem | null => {
        const pinned = pinnedFields?.includes(field) || false
        const onPinnedToggle = pinnable ? () => toggleFieldPin(field) : undefined
        switch (field) {
            case AssetField.lmid:
                return {
                    label: t('common.lmid'),
                    value: asset.lmid,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.serialNumber:
                return {
                    label: t('asset.serialNumber'),
                    value: asset.serialNo || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.serialNumberMachine:
                return {
                    label: t('asset.serialNumberMachine'),
                    value: asset.serialNoMachine || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.drawingNumber:
                return {
                    label: t('asset.drawingNumber'),
                    value: asset.drawingNo || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.manufacturerCode:
                return {
                    label: t('asset.manufacturerCode'),
                    value: asset.manufacturerCode || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.inventoryNumber:
                return {
                    label: t('asset.inventoryNumber'),
                    value: asset.inventoryNo || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.labelIds:
                return {
                    label: t('label.labelIds'),
                    value: asset.labels.length ? asset.labels.join(", ") : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.internalOrderNumber:
                return {
                    label: t('asset.internalOrderNumber'),
                    value: asset.internalOrderNumber || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.expectedToBeAvailableOn:
                return {
                    label: t('asset.expectedToBeAvailableOn'),
                    value: asset.availabilityDate ? asset.availabilityDate.format(t('config.dateLongTimeFormat')) : <NoValue />,
                    isInvalid: !!invalidFields?.availabilityDate,
                    isInvalidHighlighted: true,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.expectedToBeReturnedOn:
                return {
                    label: t('asset.expectedToBeReturnedOn'),
                    value: asset.expectedReturnDate ? asset.expectedReturnDate.format(t('config.dateLongTimeFormat')) : <NoValue />,
                    isInvalid: !!invalidFields?.expectedReturnDate,
                    isInvalidHighlighted: true,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.supplierOrderNumber:
                return {
                    label: t('asset.supplierOrderNumber'),
                    value: asset.supplierOrderNo || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.reasonForDisposal:
                if (!asset.isPurchased) return null
                return {
                    label: t('assetDisposal.reasonForDisposal'),
                    value: asset.disposal?.reason ? getAssetDisposalReasonLabel(asset.disposal?.reason) : <NoValue />,
                    isInvalid: !!invalidFields?.disposalReason,
                    isInvalidHighlighted: true,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.disposalDate:
                return {
                    label: t(asset.isPurchased ? 'assetDisposal.disposalDate' : 'assetDisposal.holdershipEndDate'),
                    value: asset.disposal?.date ? asset.disposal?.date.format(t('config.dateLongTimeFormat')) : <NoValue />,
                    isInvalid: !!invalidFields?.disposalDate,
                    isInvalidHighlighted: true,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.disposalDescription:
                return {
                    label: t(pinnedBlock ? 'assetDisposal.disposalDescription' : 'assetDisposal.description'),
                    value: asset.disposal?.description || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.features:
                const features = getTranslation(asset.features, tenantPrimaryLanguageIsoCode)
                return {
                    label: t('asset.features'),
                    value: <Flex flow="column" alignItems="flex-start">
                        {!features && <NoValue className="mb-2">{t('features.noFeaturesAdded')}</NoValue>}
                        <Features features={features} onCopyClick={onCopyFeatures ? () => onCopyFeatures(features) : undefined} />
                    </Flex>,
                    type: ListItemType.verticalSection,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.freightUnits:
                return {
                    label: t('freightUnit.freightUnits'),
                    value: asset.freightUnits && asset.freightUnits.length > 0 ? <ListKeyValue items={asset.freightUnits.map(fu => ({
                        label: <FreightUnitsItem freightUnit={fu} />
                    }))} /> : <NoValue>{t('freightUnit.noFreightUnitsAdded')}</NoValue>,
                    type: ListItemType.verticalSection,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.connectedAssets:
                if (!connectedAssets?.length) return null
                return {
                    label: t('asset.connectedAssets'),
                    value: <AssetCollectionTable connectedAssets={connectedAssets} />,
                    type: ListItemType.verticalSection,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.stateOfStorage:
                return {
                    label: t("asset.stateOfStorage.label"),
                    value: <>
                        {displayStatusLoading
                            ? <NoValue>{t("common.loading")}</NoValue>
                            : <>
                                {!!displayStatus?.status && <AssetStoredStatus status={displayStatus.status} />}
                                <br />
                                {displayStatus?.locationName}
                            </>
                        }
                        {eventAddress && !eventAddress.isEmpty() &&
                            <div className="white-space-pre-line font-regular mt-half">
                                {eventAddress.print()}
                            </div>
                        }
                        {!!asset.lastScanDate
                            && <Text type="pSmallest" className="color-grey-500 mt-half">
                                {t("asset.lastScannedOn")} {asset.lastScanDate.format(t('config.dateLongTimeFormat'))}
                            </Text>
                        }
                    </>,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.coordinates:
                return {
                    label: t("common.coordinates"),
                    value: asset.assetLocation ? `${asset.assetLocation.location.lat}, ${asset.assetLocation.location.lng}` : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.homebase:
                return {
                    label: t("asset.homebase"),
                    value: homebaseWarehouseDescription === null
                        ? <NoValue>{t("common.loading")}</NoValue>
                        : homebaseWarehouseDescription ?? <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.shelfStorage:
                if (!asset.assetStorage?.title) return null
                return {
                    label: t("asset.shelfStorage"),
                    value: asset.assetStorage.title,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.shelfStorageDescription:
                if (!asset.assetStorage?.description) return null
                return {
                    label: t("asset.additionalShelfStorageDescription"),
                    value: asset.assetStorage.description,
                    type: ListItemType.vertical,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.profitCenter:
                return {
                    label: t("profitCenters.profitCenter"),
                    value: asset.profitCenterDescription || <NoValue />,
                    isInvalid: !!invalidFields?.profitCenter,
                    isInvalidHighlighted: true,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.purpose:
                return {
                    label: t("asset.purpose"),
                    value: asset.purposes && asset.purposes.length > 0 ? asset.purposes.map(p => getPurposeLabel(p)).join(", ") : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.currentHolder:
                if (asset.ownershipType !== AssetOwnershipType.purchased && article.articleType !== ArticleType.machine) return null
                return {
                    label: t("asset.currentHolder"),
                    value: asset.holderName || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.lendingCompanyName:
                if (!asset.isBorrowed) return null
                return {
                    label: t('common.name'),
                    subLabel: pinnedBlock ? t('asset.lendingCompany') : undefined,
                    value: asset.owner?.name || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.lendingCompanyAddress:
                if (!asset.isBorrowed) return null
                return {
                    label: t('asset.subsidiaryAddress'),
                    subLabel: pinnedBlock ? t('asset.lendingCompany') : undefined,
                    value: asset.owner?.address?.print() || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.lendingCompanyContactPerson:
                if (!asset.isBorrowed) return null
                return {
                    label: t('asset.contactPerson'),
                    subLabel: pinnedBlock ? t('asset.lendingCompany') : undefined,
                    value: asset.owner?.customerContact?.name || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.lendingCompanyTelephone:
                if (!asset.isBorrowed) return null
                return {
                    label: t('asset.telephoneNumber'),
                    subLabel: pinnedBlock ? t('asset.lendingCompany') : undefined,
                    value: asset.owner?.customerContact?.telephone || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.lendingCompanyEmail:
                if (!asset.isBorrowed) return null
                return {
                    label: t('asset.emailAddress'),
                    subLabel: pinnedBlock ? t('asset.lendingCompany') : undefined,
                    value: asset.owner?.customerContact?.email || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.buyBackCustomer:
                if (!asset.buyBack) return null
                return {
                    label: t('asset.customer'),
                    subLabel: pinnedBlock ? t('asset.buyBack') : undefined,
                    value: asset.buyBack.customerName,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.buyBackSalesPerson:
                if (!asset.buyBack) return null
                return {
                    label: `${t('asset.salesPerson')}${pinnedBlock ? ` (${t('asset.buyBack')})` : ''}`,
                    value: asset.buyBack.salesPersonNumber ? tenantEmployees?.find(employee => employee.number === asset.buyBack?.salesPersonNumber)?.print() || <NoValue /> : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.buyBackReference:
                if (!asset.buyBack) return null
                return {
                    label: t('asset.buyBackReference'),
                    value: asset.buyBack.reference || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.relevantForSalesStatistic:
                if (!initialSaleOrRental) return null
                return {
                    label: t('asset.relevantForSalesStatistic'),
                    value: initialSaleOrRental.relevantForAssetStatistic ? t('common.yes') : t('common.no'),
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.relatedRequest:
                if (!initialSaleOrRental) return null
                return {
                    label: t('asset.relatedRequest'),
                    value: initialSaleOrRental.relevantForAssetStatistic && initialSaleOrRental.reservation
                        ? <FlexibleContent
                            text={initialSaleOrRental.reservation.number}
                            onClick={() => initialSaleOrRental.reservation?.id && reservationSidebars.show({ reservationId: initialSaleOrRental.reservation.id })}
                            className="font-regular"
                        />
                        : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.reportAsset:
                if (!initialSaleOrRental) return null
                return {
                    label: t('asset.reportAsset'),
                    value: initialSaleOrRental.relevantForAssetStatistic && initialSaleOrRental.reportAsset
                        ? t('common.yes')
                        : t('common.no'),
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.internalNotes:
                return {
                    label: t('asset.internalNotes'),
                    value: asset.internalNotes || <NoValue>{t('asset.noNotes')}</NoValue>,
                    type: ListItemType.vertical,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.constructionYear:
                return {
                    label: t('asset.yearOfConstruction'),
                    value: asset.constructionYear,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.operatingHours:
                return {
                    label: t('asset.operatingHours'),
                    toolbar: operationHoursLogButton,
                    value: <ListKeyValue items={[
                        {
                            label: t('asset.currentOperatingHours'),
                            value: typeof asset.operationHours === "number" ? t('common.xHours', { count: asset.operationHours }) : <NoValue />,
                            valueTestId: "asset-operation-hours"
                        },
                        {
                            label: t('asset.recordedOn'),
                            value: asset.operationHoursTime ? asset.operationHoursTime.format(t('config.dateLongTimeFormat')) : <NoValue />,
                            valueTestId: "asset-operation-hours-time"
                        },
                    ]} />,
                    type: ListItemType.verticalSection,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.operationalState:
                return {
                    label: t('asset.operationalState'),
                    value: (asset.isOperational ? t('asset.assetOperational') : t('asset.assetOutOfOrder')),
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.condition:
                return {
                    label: t('asset.condition'),
                    value: typeof asset.condition === "number" ? `${asset.condition}%` : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.referenceNumberSale:
                return {
                    label: t('asset.referenceNumberSale'),
                    value: asset.assetFinancial.referenceNumberSale,
                    subLabel: asset.assetFinancial.initialReferenceNumberSale ? t('asset.initialReferenceNumberSale') : undefined,
                    subValue: asset.assetFinancial.initialReferenceNumberSale ?? undefined,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.referenceNumberRental:
                return {
                    label: t('asset.referenceNumberRental'),
                    value: asset.assetFinancial.referenceNumberRental || <NoValue />,
                    subLabel: asset.assetFinancial.initialReferenceNumberRental ? t('asset.initialReferenceNumberRental') : undefined,
                    subValue: asset.assetFinancial.initialReferenceNumberRental ?? undefined,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.referenceValue:
                return {
                    label: t('asset.referenceValue'),
                    value: asset.assetFinancial.referenceValue?.print() || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.monthlyRentalCosts:
                if (!asset.isBorrowed) return null
                return {
                    label: t('asset.monthlyRentalCosts.label'),
                    value: asset.assetFinancial.monthlyRentalCost?.rentalCost?.print() || <NoValue />,
                    subValue: asset.assetFinancial.monthlyRentalCost?.validFrom ?
                        t('asset.monthlyRentalCosts.validFrom', { validFrom: asset.assetFinancial.monthlyRentalCost?.validFrom.format(t('config.dateLongFormat')) })
                        : undefined,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.referenceValueCorrection:
                if (!asset.buyBack) return null
                return {
                    label: t('asset.referenceValueCorrection'),
                    value: (asset.assetFinancial.referenceValueCorrection || asset.assetFinancial.referenceValueCorrection === 0) ? `${asset.assetFinancial.referenceValueCorrection} ${currency}` : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.referenceValueCorrected:
                if (!asset.buyBack) return null
                return {
                    label: t('asset.referenceValueCorrected'),
                    value: (asset.assetFinancial.referenceValue?.amount || asset.assetFinancial.referenceValue?.amount === 0) ?
                        t('common.amountWithCurrency',
                            {
                                count: asset.assetFinancial.referenceValue.amount + (asset.assetFinancial.referenceValueCorrection || 0),
                                currency,
                            })
                        :
                        <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.listPrice:
                return {
                    label: t('asset.listPrice'),
                    value: typeof asset.assetListPrice?.listPrice === "number" ? t('common.amountWithCurrency', { count: asset.assetListPrice.listPrice, currency }) : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.listPriceDate:
                return {
                    label: t('asset.listPriceDate'),
                    value: asset.assetListPrice?.listPriceDate ? asset.assetListPrice.listPriceDate.format(t('config.dateLongFormat')) : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.listPriceDiscount:
                const _discountAmount = discountCalculated === undefined ? asset.assetListPrice?.discountAmount : discountCalculated
                return {
                    label: t('asset.discount'),
                    subLabel: typeof _discountAmount === "number" ? t('asset.discountAmountCalculated') : undefined,
                    value: typeof asset.assetListPrice?.discount === "number" ? `${asset.assetListPrice.discount}%` : <NoValue />,
                    subValue: typeof _discountAmount === "number" ? t('common.amountWithCurrency', { count: _discountAmount, currency }) : undefined,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.listPriceSalesSupport:
                return {
                    label: t('asset.salesSupport'),
                    value: typeof asset.assetListPrice?.salesSupport === "number" ? t('common.amountWithCurrency', { count: asset.assetListPrice.salesSupport, currency }) : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.recommendedSalesPrice:
                return {
                    label: t('asset.recommendedSalesPrice'),
                    value: typeof _salesPrice === "number" ? t('common.amountWithCurrency', { count: _salesPrice, currency }) : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.recommendedSalesPriceAdjusted:
                if (typeof asset.condition !== "number") return null
                const _salesPriceAdjustedConditionValueEquipment = typeof _salesPriceAdjustedCondition === "number" ? t('common.amountWithCurrency', { count: _salesPriceAdjustedCondition, currency }) : <NoValue />
                const _salesPriceAdjustedConditionValueMachine = <NoValue>{t("common.notAvailable")}</NoValue>
                return {
                    label: t('asset.recommendedSalesPriceAdjustedForCondition_conditioned', { condition: asset.condition }),
                    value: article?.articleType === ArticleType.machine ? _salesPriceAdjustedConditionValueMachine : _salesPriceAdjustedConditionValueEquipment,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.initialCondition:
                return {
                    label: t('asset.initialCondition'),
                    value: (asset.isSecondhand ? t('asset.usedAsset') : t('asset.newAsset')),
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.assetOrigin:
                return {
                    label: t('asset.origin'),
                    value: asset.origin ? (t(`assetOrigin.${asset.origin}`)) : <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.productGroup:
                const hasProductsLink = checkGlobalPermission({
                    service: ApiServiceName.asset,
                    entity: ApiLinkEntity.assetProduct,
                    rel: ApiLinkRel.all,
                })
                return {
                    label: t('asset.productGroup'),
                    value: asset.getProductGroupNumber(!hasProductsLink) || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.assetCategory:
                return {
                    label: t('asset.assetCategory'),
                    value: asset.assetFinancial.assetCategory?.description || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetField.assetType:
                return {
                    label: t('asset.assetType'),
                    value: asset.assetFinancial.assetType?.description || <NoValue />,
                    pinned,
                    onPinnedToggle,
                }
            case AssetTechnicalDataField.color:
            case AssetTechnicalDataField.emissionLevel:
            case AssetTechnicalDataField.dimensions:
            case AssetTechnicalDataField.volume:
            case AssetTechnicalDataField.weight:
            case AssetTechnicalDataField.quickCouplingSystem:
            case AssetTechnicalDataField.connectionType:
            case AssetTechnicalDataField.boltOnCuttingEdge:
            case AssetTechnicalDataField.teeth:
            case AssetTechnicalDataField.heavyDuty:
            case AssetTechnicalDataField.locking:
            case AssetTechnicalDataField.controlSystem:
            case AssetTechnicalDataField.agility:
                return getTechnicalDataField(field)
            default:
                return null
        }
    }, [_salesPrice, _salesPriceAdjustedCondition, article.articleType, asset, checkGlobalPermission, connectedAssets,
        currency, discountCalculated, displayStatus?.locationName, displayStatus?.status, displayStatusLoading,
        getTechnicalDataField, homebaseWarehouseDescription, initialSaleOrRental, invalidFields?.availabilityDate,
        invalidFields?.disposalDate, invalidFields?.disposalReason, invalidFields?.expectedReturnDate,
        invalidFields?.profitCenter, operationHoursLogButton, pinnable, pinnedBlock, pinnedFields, t, tenantEmployees,
        tenantPrimaryLanguageIsoCode, toggleFieldPin, onCopyFeatures, eventAddress])

    const getFields = useCallback((fields: AssetField[]) => {
        const items: IListItem[] = []
        for (const field of fields) {
            const item = getField(field)
            if (!item) continue
            items.push(item)
        }
        return items
    }, [getField])

    return {
        getField,
        getFields,
    }
}
